/**
 * The decrypted view of the file tree.
 *
 * This is where the two halves meet: `api.ts` moves ciphertext, `crypto/` holds
 * keys, and nothing above this file has to think about either. Screens work
 * with `DecryptedNode` — a node with a readable name — and call the operations
 * here, which handle the key chaining that every write implies.
 *
 * The chaining is the part worth understanding. A node's key is wrapped under
 * its **parent's** key, so reaching a node means walking down from the root
 * unwrapping as you go. That is what makes sharing a folder mean "and
 * everything in it", and it is why moving a node is not a simple UPDATE: the
 * key has to be re-wrapped under the new parent before the server will take it.
 *
 * Keys are cached per node for the life of the unlocked vault. Re-deriving a
 * path on every render would mean an AES unwrap per ancestor per node, which is
 * fast but not free, and the cache is dropped wholesale when the vault locks.
 */

import { api, type NodeWire, type Role, type SharedWithMeWire } from './api';
import { requireVault } from './crypto/vault';
import {
  unwrapContentKey,
  unwrapNodeKey,
  wrapContentKey,
  wrapNodeKey,
  generateContentKey,
  generateNodeKey
} from './crypto/keys';
import { unwrapKeyFromSender, wrapKeyForRecipient } from './crypto/share';
import {
  assertValidName,
  decryptMime,
  decryptName,
  encryptMime,
  encryptName,
  nameHmac
} from './crypto/metadata';
import {
  CHUNK_SIZE,
  decryptToBlob,
  decryptStream,
  encryptFile,
  newNonceBase,
  planUpload,
  type ChunkContext
} from './crypto/stream';
import { fromBase64, toBase64 } from './crypto/primitives';
import { decryptThumbnail } from './thumbnails';

export interface DecryptedNode {
  id: string;
  parentId: string | null;
  kind: 'folder' | 'file';
  name: string;
  mime: string | null;
  sizeBytes: number;
  plainSizeBytes: number;
  hasThumbnail: boolean;
  hasContent: boolean;
  trashedAt: number | null;
  createdAt: number;
  updatedAt: number;
  role: Role;
  /** Set when a name could not be decrypted — see `decryptNode`. */
  undecryptable?: boolean;
}

// ── the key cache ───────────────────────────────────────────────────────────

const nodeKeys = new Map<string, CryptoKey>();

/** Dropped when the vault locks, so keys never outlive the session that opened
 *  them. Called by the vault subscription in state.tsx. */
export function forgetKeys(): void {
  nodeKeys.clear();
}

/**
 * How to reach a workspace root's key, registered by `workspaces.ts`.
 *
 * A hook rather than an import because the dependency only runs one way:
 * `workspaces.ts` is built on this module, and this module should not know that
 * workspaces exist beyond "some roots are opened by someone else". The same
 * reasoning keeps `crypto/vault.ts` free of any dependency on the data layer.
 */
type WorkspaceKeyResolver = (workspaceId: string) => Promise<CryptoKey>;
let resolveWorkspaceKey: WorkspaceKeyResolver | null = null;

export function setWorkspaceKeyResolver(resolver: WorkspaceKeyResolver): void {
  resolveWorkspaceKey = resolver;
}

export function cacheNodeKey(nodeId: string, key: CryptoKey): void {
  nodeKeys.set(nodeId, key);
}

/**
 * A node's key, if it has already been unwrapped.
 *
 * Rows on screen always have one: `listFolder` walks the chain for every child
 * it decrypts, and `uploadFile` caches the key it generated. Returning null
 * rather than unwrapping on demand keeps this synchronous for render paths and
 * makes a miss obvious rather than silently expensive.
 */
export function getCachedKey(nodeId: string): CryptoKey | null {
  return nodeKeys.get(nodeId) ?? null;
}

/**
 * The key for a node, unwrapping down from wherever the cache runs out.
 *
 * The root's key is wrapped under the master key; everything else under its
 * parent. `chain` is the node plus its ancestors, nearest first, which is
 * exactly what the list and get endpoints already return — so this costs no
 * extra requests.
 *
 * A workspace root is the one exception, and it is handled before the walk
 * starts rather than inside it. Its key is not wrapped under anybody's master
 * key — it arrives ECIES-wrapped on the caller's membership row — so the walk
 * would otherwise reach for `vault.masterKey` and produce nothing that opens.
 * Seeding it first is what makes every operation below work inside a workspace
 * with no further changes, including a cold reload straight onto a deep folder.
 */
export async function keyForNode(node: NodeWire, chain: NodeWire[]): Promise<CryptoKey> {
  const cached = nodeKeys.get(node.id);
  if (cached) return cached;

  const vault = requireVault();

  // Root first, then down. `chain` arrives nearest-first, so walk it backwards.
  const path = [...chain].reverse().concat(node);
  let parentKey: CryptoKey | null = null;

  const root = path[0];
  if (root && root.parentId === null && root.workspaceId && !nodeKeys.has(root.id)) {
    if (!resolveWorkspaceKey) {
      throw new Error('This file is in a workspace, and no workspace key resolver is registered.');
    }
    await resolveWorkspaceKey(root.workspaceId);
  }

  for (const step of path) {
    const known = nodeKeys.get(step.id);
    if (known) {
      parentKey = known;
      continue;
    }
    // A node with no parent is the root, and its key hangs off the master key.
    const wrappingKey = step.parentId === null ? vault.masterKey : parentKey;
    if (!wrappingKey) {
      throw new Error(`Cannot reach the key for ${step.id}: its parent is missing from the chain.`);
    }
    const key = await unwrapNodeKey(wrappingKey, step.nodeKeyWrapped);
    nodeKeys.set(step.id, key);
    parentKey = key;
  }

  const key = nodeKeys.get(node.id);
  if (!key) throw new Error('Key chain did not reach the node.');
  return key;
}

// ── decryption ──────────────────────────────────────────────────────────────

/**
 * Turn a wire node into a readable one.
 *
 * A failure here is not fatal to the listing. One node whose name will not
 * decrypt — a key rotated on another device, a half-finished write — should not
 * blank the whole folder, so it comes back marked `undecryptable` and the UI
 * renders it greyed with its id. Silently dropping it would be worse: the file
 * still occupies quota and the user needs to be able to delete it.
 */
export async function decryptNode(node: NodeWire, key: CryptoKey): Promise<DecryptedNode> {
  const base = {
    id: node.id,
    parentId: node.parentId,
    kind: node.kind,
    sizeBytes: node.sizeBytes,
    plainSizeBytes: node.plainSizeBytes,
    hasThumbnail: node.hasThumbnail,
    hasContent: !!node.currentRevisionId,
    trashedAt: node.trashedAt,
    createdAt: node.createdAt,
    updatedAt: node.updatedAt,
    role: node.role
  };

  try {
    return {
      ...base,
      name: await decryptName(key, node.nameEnc),
      mime: node.mimeEnc ? await decryptMime(key, node.mimeEnc) : null
    };
  } catch {
    return { ...base, name: 'Could not be read', mime: null, undecryptable: true };
  }
}

export interface FolderContents {
  parent: DecryptedNode;
  /** Nearest first, so a breadcrumb renders them reversed. */
  ancestors: DecryptedNode[];
  nodes: DecryptedNode[];
}

/**
 * List a folder, decrypted and sorted.
 *
 * Sorting happens here rather than in SQL because the server cannot read names.
 * Folders first, then by name — the ordering a file browser is expected to have
 * and one the API could never provide.
 */
export async function listFolder(folderId: string): Promise<FolderContents> {
  const { parent, ancestors, nodes } = await api.nodes.list(folderId);

  const parentKey = await keyForNode(parent, ancestors);
  const chainForChildren = [parent, ...ancestors];

  const decrypted = await Promise.all(
    nodes.map(async (n) => decryptNode(n, await keyForNode(n, chainForChildren)))
  );

  const ancestorNodes = await Promise.all(
    ancestors.map(async (a, i) => decryptNode(a, await keyForNode(a, ancestors.slice(i + 1))))
  );

  return {
    parent: await decryptNode(parent, parentKey),
    ancestors: ancestorNodes,
    nodes: decrypted.sort(compareNodes)
  };
}

/** Folders before files, then by name, case-insensitively. */
export const compareNodes = (a: DecryptedNode, b: DecryptedNode): number =>
  a.kind === b.kind
    ? a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' })
    : a.kind === 'folder'
      ? -1
      : 1;

// ── writes ──────────────────────────────────────────────────────────────────

/**
 * Create a folder and return both halves of it.
 *
 * The wire node is what an ancestor chain is made of — `keyForNode` walks
 * `NodeWire`s, not decrypted ones — so anything that creates a folder and then
 * puts something *inside* it needs this rather than `createFolder`. Splitting
 * the two beats re-fetching the node that was just created.
 */
async function createFolderNode(
  parent: NodeWire,
  chain: NodeWire[],
  name: string
): Promise<{ wire: NodeWire; node: DecryptedNode }> {
  const clean = assertValidName(name);
  const parentKey = await keyForNode(parent, chain);
  const nodeKey = await generateNodeKey();

  const { node } = await api.nodes.createFolder({
    parentId: parent.id,
    nameEnc: await encryptName(nodeKey, clean),
    nameHmac: await nameHmac(parentKey, clean),
    nodeKeyWrapped: await wrapNodeKey(parentKey, nodeKey)
  });

  cacheNodeKey(node.id, nodeKey);
  return { wire: node, node: await decryptNode(node, nodeKey) };
}

export async function createFolder(parent: NodeWire, chain: NodeWire[], name: string) {
  return (await createFolderNode(parent, chain, name)).node;
}

export async function renameNode(
  node: NodeWire,
  chain: NodeWire[],
  parentKey: CryptoKey,
  name: string
): Promise<void> {
  const clean = assertValidName(name);
  const nodeKey = await keyForNode(node, chain);

  await api.nodes.update(node.id, {
    nameEnc: await encryptName(nodeKey, clean),
    nameHmac: await nameHmac(parentKey, clean)
  });
}

/**
 * Move a node to another folder.
 *
 * Three things change together and all three have to: the parent, the key
 * (re-wrapped under the new parent) and the name HMAC (recomputed under the new
 * parent's key, since sibling uniqueness is scoped to a folder). Sending fewer
 * than all three leaves a node the client can no longer open.
 */
export async function moveNode(
  node: NodeWire,
  chain: NodeWire[],
  name: string,
  newParent: NodeWire,
  newParentChain: NodeWire[]
): Promise<void> {
  const nodeKey = await keyForNode(node, chain);
  const newParentKey = await keyForNode(newParent, newParentChain);

  await api.nodes.update(node.id, {
    parentId: newParent.id,
    nodeKeyWrapped: await wrapNodeKey(newParentKey, nodeKey),
    nameEnc: await encryptName(nodeKey, name),
    nameHmac: await nameHmac(newParentKey, name)
  });
}

/**
 * Resolve — creating as needed — the folder a dropped file belongs in.
 *
 * A dropped folder arrives as a flat list of files each carrying a relative
 * path, so the tree has to be built one node at a time: `POST /api/cloud/nodes`
 * makes a single folder, and each folder's key is wrapped under its parent's,
 * so there is no batch shortcut that would not also mean the server choosing
 * keys.
 *
 * ── Two things this must get right ──────────────────────────────────────────
 *  1. **Reuse an existing folder rather than failing.** Dropping "assets" twice
 *     should merge into the folder that is already there. The sibling-name
 *     constraint is on `name_hmac`, so an existing child is found by comparing
 *     decrypted names — the server cannot help.
 *  2. **Cache within one drop.** A folder of 300 files in 12 directories would
 *     otherwise ask the server to create the same 12 folders 300 times, and
 *     lose the race with itself. `cache` is keyed by the joined path and is
 *     scoped to a single drop by its caller.
 *
 * Returns the parent to upload into, and the ancestor chain `uploadFile` needs.
 */
export async function ensureFolderPath(
  root: NodeWire,
  rootChain: NodeWire[],
  path: string[],
  cache: Map<string, { parent: NodeWire; chain: NodeWire[] }>
): Promise<{ parent: NodeWire; chain: NodeWire[] }> {
  let parent = root;
  let chain = rootChain;

  for (let depth = 0; depth < path.length; depth++) {
    const key = path.slice(0, depth + 1).join('/');
    const hit = cache.get(key);
    if (hit) {
      parent = hit.parent;
      chain = hit.chain;
      continue;
    }

    const name = path[depth]!;
    const childChain = [parent, ...chain];

    // Look before creating. A second drop of the same folder must merge, not
    // 409 — and the check has to happen on decrypted names.
    const { nodes } = await api.nodes.list(parent.id);
    let found: NodeWire | undefined;
    for (const candidate of nodes) {
      if (candidate.kind !== 'folder') continue;
      const decrypted = await decryptNode(candidate, await keyForNode(candidate, childChain));
      if (decrypted.name.toLowerCase() === name.toLowerCase()) {
        found = candidate;
        break;
      }
    }

    if (!found) found = (await createFolderNode(parent, chain, name)).wire;

    parent = found;
    chain = childChain;
    cache.set(key, { parent, chain });
  }

  return { parent, chain };
}

// ── upload ──────────────────────────────────────────────────────────────────

export interface UploadProgress {
  file: string;
  bytesDone: number;
  bytesTotal: number;
  chunk: number;
  chunks: number;
}

/**
 * Encrypt a file and upload it.
 *
 * The order is fixed by the server's handshake and by the AAD: the revision id
 * is part of what every chunk is authenticated against, so it has to exist
 * before the first byte is encrypted. That is why the upload is opened with a
 * *plan* — sizes and chunk counts computed without encrypting — and the bytes
 * follow.
 *
 * On any failure the upload is aborted so the reservation it holds is released
 * rather than waiting for the hourly sweep.
 */
export async function uploadFile(
  file: File,
  parent: NodeWire,
  chain: NodeWire[],
  options: { onProgress?: (p: UploadProgress) => void; existingNodeId?: string } = {}
): Promise<DecryptedNode> {
  const clean = assertValidName(file.name);
  const parentKey = await keyForNode(parent, chain);

  /**
   * Replacing keeps the node's existing keys; only a new file gets new ones.
   *
   * This is not an optimisation, it is a correctness requirement. On the
   * replace path the server leaves `node_key_wrapped` and `content_key_wrapped`
   * exactly as they were — it reuses the row — so a fresh content key here
   * would encrypt the new chunks under a key nobody can reach, and every kept
   * revision would stop opening too, since they all share the node's one
   * content key. `existingNodeId` was declared and never called; wired up
   * naively it would have written unreadable files.
   */
  let nodeKey: CryptoKey;
  let contentKey: CryptoKey;
  // The wrapped forms are carried, not recomputed. An unwrapped key here is
  // non-extractable — deliberately, see SECURITY.md — so `exportKey` on it
  // throws, and re-wrapping is exactly what that prevents. On the replace path
  // the server keeps the row's own values anyway, so the honest thing is to
  // send back what it already has.
  let nodeKeyWrapped: string;
  let contentKeyWrapped: string;

  if (options.existingNodeId) {
    const { node, ancestors } = await api.nodes.get(options.existingNodeId);
    if (!node.contentKeyWrapped) throw new Error('That file cannot be replaced.');
    nodeKey = await keyForNode(node, ancestors);
    contentKey = await unwrapContentKey(nodeKey, node.contentKeyWrapped);
    nodeKeyWrapped = node.nodeKeyWrapped;
    contentKeyWrapped = node.contentKeyWrapped;
  } else {
    nodeKey = await generateNodeKey();
    contentKey = await generateContentKey();
    nodeKeyWrapped = await wrapNodeKey(parentKey, nodeKey);
    contentKeyWrapped = await wrapContentKey(nodeKey, contentKey);
  }

  const plan = planUpload(file.size, CHUNK_SIZE);
  // Fresh per revision, never reused: the same (key, IV) pair across two
  // revisions under one content key would break GCM outright.
  const nonceBase = newNonceBase();

  const opened = await api.uploads.start({
    parentId: parent.id,
    nodeId: options.existingNodeId,
    nameEnc: await encryptName(nodeKey, clean),
    nameHmac: await nameHmac(parentKey, clean),
    mimeEnc: await encryptMime(nodeKey, file.type || 'application/octet-stream'),
    // Ignored by the server on the replace path, which keeps the row's own
    // values — sent anyway so one request shape covers both cases.
    nodeKeyWrapped,
    contentKeyWrapped,
    chunkCount: plan.chunkCount,
    chunkSize: plan.chunkSize,
    plainBytes: plan.plainBytes,
    cipherBytes: plan.cipherBytes,
    nonceBase: toBase64(nonceBase)
  });

  const ctx: ChunkContext = {
    contentKey,
    revisionId: opened.revisionId,
    nonceBase,
    totalChunks: plan.chunkCount,
    chunkSize: plan.chunkSize
  };

  try {
    for await (const { index, bytes } of encryptFile(file, ctx)) {
      await api.uploads.part(opened.uploadId, index, bytes);
      options.onProgress?.({
        file: clean,
        bytesDone: Math.min((index + 1) * plan.chunkSize, file.size),
        bytesTotal: file.size,
        chunk: index + 1,
        chunks: plan.chunkCount
      });
    }

    const { node } = await api.uploads.complete(opened.uploadId);
    cacheNodeKey(node.id, nodeKey);
    return decryptNode(node, nodeKey);
  } catch (err) {
    // Best-effort: if this fails too, the hourly sweep still reclaims the bytes.
    await api.uploads.abort(opened.uploadId).catch(() => {});
    throw err;
  }
}

// ── download ────────────────────────────────────────────────────────────────

/**
 * The decryption context for a file, current version or a named older one.
 *
 * The content key comes off the *node*, not the revision — every version of a
 * file shares one — so reading history costs no extra key work. Everything that
 * differs per revision (the nonce base, the chunk count, the AAD's revision id)
 * comes from the manifest, which is why the revision has to be named to both
 * calls or the chunks will not authenticate.
 */
async function contextFor(
  nodeId: string,
  nodeKey: CryptoKey,
  revisionId?: string
): Promise<ChunkContext> {
  const { node, revision } = await api.nodes.manifest(nodeId, revisionId);
  if (!node.contentKeyWrapped) throw new Error('That file has no contents.');

  return {
    contentKey: await unwrapContentKey(nodeKey, node.contentKeyWrapped),
    revisionId: revision.id,
    nonceBase: fromBase64(revision.nonceBase),
    totalChunks: revision.chunkCount,
    chunkSize: revision.chunkSize
  };
}

/** A file's plaintext as a stream, decrypted chunk by chunk as it arrives. */
export async function downloadStream(
  nodeId: string,
  nodeKey: CryptoKey,
  onProgress?: (done: number, total: number) => void
): Promise<ReadableStream<Uint8Array>> {
  const ctx = await contextFor(nodeId, nodeKey);
  return decryptStream(ctx, (i) => api.nodes.chunk(nodeId, i), onProgress);
}

/**
 * A file's plaintext as an object URL, for showing it in the page.
 *
 * The MIME travels with the blob — `decryptToBlob` takes it — so an `<img>` or
 * `<video>` pointed at the result behaves exactly as it would for a normal
 * file, and the bytes never touch the network in the clear.
 *
 * The caller owns the URL and must `URL.revokeObjectURL` it. An object URL
 * pins its blob in memory for the life of the document, so leaking one leaks
 * the whole decrypted file.
 */
export async function previewUrl(
  node: DecryptedNode,
  nodeKey: CryptoKey,
  onProgress?: (done: number, total: number) => void
): Promise<string> {
  const ctx = await contextFor(node.id, nodeKey);
  let done = 0;
  const blob = await decryptToBlob(
    ctx,
    async (i) => {
      const chunk = await api.nodes.chunk(node.id, i);
      onProgress?.(++done, ctx.totalChunks);
      return chunk;
    },
    node.mime ?? 'application/octet-stream'
  );
  return URL.createObjectURL(blob);
}

/** A text file's contents, for the text viewer. Bounded by the caller. */
export async function previewText(node: DecryptedNode, nodeKey: CryptoKey): Promise<string> {
  const ctx = await contextFor(node.id, nodeKey);
  const blob = await decryptToBlob(ctx, (i) => api.nodes.chunk(node.id, i), 'text/plain');
  return blob.text();
}

/**
 * Save a file to disk.
 *
 * Buffers through a Blob rather than streaming to the download: a
 * `showSaveFilePicker` path would stream properly but is Chromium-only, and a
 * failed integrity check part-way through a streamed save would leave a
 * truncated file on the user's disk that looked complete. Buffering means a
 * tampered chunk fails before anything is written.
 */
/** One older version, saved to disk under a name that says which it is. */
export async function downloadRevision(
  node: DecryptedNode,
  nodeKey: CryptoKey,
  revisionId: string,
  createdAt: number
): Promise<void> {
  const ctx = await contextFor(node.id, nodeKey, revisionId);
  const blob = await decryptToBlob(
    ctx,
    (i) => api.nodes.chunk(node.id, i, revisionId),
    node.mime ?? 'application/octet-stream'
  );

  // "report.pdf" saved from an old version becomes "report (2026-07-14).pdf",
  // so it does not silently overwrite the current one in the download folder.
  const stamp = new Date(createdAt * 1000).toISOString().slice(0, 10);
  const dot = node.name.lastIndexOf('.');
  const named =
    dot > 0 ? `${node.name.slice(0, dot)} (${stamp})${node.name.slice(dot)}` : `${node.name} (${stamp})`;

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = named;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function downloadToDisk(
  node: DecryptedNode,
  nodeKey: CryptoKey,
  onProgress?: (done: number, total: number) => void
): Promise<void> {
  const ctx = await contextFor(node.id, nodeKey);
  const blob = await decryptToBlob(
    ctx,
    async (i) => {
      onProgress?.(i + 1, ctx.totalChunks);
      return api.nodes.chunk(node.id, i);
    },
    node.mime ?? 'application/octet-stream'
  );

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = node.name;
  a.click();
  // Revoked on the next tick: revoking synchronously races the click in Safari.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** A decrypted thumbnail as an object URL, or null. Callers must revoke it. */
export async function thumbnailUrl(nodeId: string, nodeKey: CryptoKey): Promise<string | null> {
  try {
    const encrypted = await api.nodes.thumbnail(nodeId);
    return URL.createObjectURL(await decryptThumbnail(new Uint8Array(encrypted), nodeKey));
  } catch {
    return null;
  }
}

// ── shares ──────────────────────────────────────────────────────────────────

/**
 * Open something shared with me.
 *
 * The key came wrapped to this account's public key rather than chained under a
 * parent — a recipient does not hold the folder above the thing they were
 * given, and that is the point.
 */
export async function openSharedNode(share: SharedWithMeWire): Promise<DecryptedNode | null> {
  if (!share.nodeKeyWrapped) return null;

  const vault = requireVault();
  const nodeKey = await unwrapKeyFromSender(vault.privateKey, share.nodeKeyWrapped);
  cacheNodeKey(share.nodeId, nodeKey);

  return decryptNode(
    {
      id: share.nodeId,
      parentId: null,
      // A share is reached through its own envelope, never through a workspace
      // membership — so this synthesised node deliberately carries none, and
      // the seam in `keyForNode` stays out of the way.
      workspaceId: null,
      kind: share.kind,
      nodeKeyWrapped: share.nodeKeyWrapped,
      nameEnc: share.nameEnc,
      mimeEnc: share.mimeEnc,
      contentKeyWrapped: share.contentKeyWrapped,
      sizeBytes: share.sizeBytes,
      plainSizeBytes: share.plainSizeBytes,
      hasThumbnail: share.hasThumbnail,
      currentRevisionId: share.contentKeyWrapped ? 'shared' : null,
      trashedAt: null,
      createdAt: share.sharedAt,
      updatedAt: share.updatedAt,
      role: share.role
    },
    nodeKey
  );
}

/**
 * Finish the invitations that were waiting for a recipient to exist.
 *
 * You cannot encrypt to a key that has not been generated, so inviting someone
 * without a vault records the invite and nothing more. The moment they sign up
 * a public key appears, and the owner's browser — the only party holding the
 * node key — has to do the wrap. Nobody at Mauvely can do it instead.
 *
 * This is the half that was missing. The endpoint and the API binding both
 * existed; nothing ever called them, so a share to someone without an account
 * looked like it worked and then stayed unreadable forever, even after they
 * joined. It failed quietly, which is why the report described it as the case
 * that *didn't* break.
 *
 * Best-effort by design: it runs on unlock behind the UI and one failure must
 * not stop the rest. Returns how many were completed, for the caller to show.
 */
export async function completePendingShares(): Promise<number> {
  requireVault();

  let pending: Awaited<ReturnType<typeof api.shares.pendingKeys>>['pending'];
  try {
    ({ pending } = await api.shares.pendingKeys());
  } catch {
    return 0;
  }
  if (!pending.length) return 0;

  let completed = 0;
  for (const item of pending) {
    try {
      // The node key is chained under its parents, so the ancestry has to come
      // back with it — `keyForNode` walks down from the root unwrapping.
      const { node, ancestors } = await api.nodes.get(item.nodeId);
      const nodeKey = await keyForNode(node, ancestors);
      const wrapped = await wrapKeyForRecipient(item.publicKey, nodeKey);

      // Re-inviting updates the existing row in place rather than adding a
      // second grant, so posting through `invite` is the completion path.
      await api.shares.invite(item.nodeId, {
        email: item.email,
        role: item.role,
        nodeKeyWrapped: wrapped,
        recipientFingerprint: item.fingerprint
      });
      completed++;
    } catch {
      // A node since deleted, a key that will not chain, a rate limit. The next
      // unlock tries again; the invite stays pending until one succeeds.
    }
  }

  return completed;
}

/**
 * Search, client-side.
 *
 * Names are encrypted, so the server cannot help. This walks the tree from the
 * root, decrypting as it goes, and filters in memory. Fine for a personal
 * drive; it is the wrong shape at scale and is recorded as such in the backlog.
 */
export async function searchTree(
  rootId: string,
  query: string,
  limit = 100
): Promise<DecryptedNode[]> {
  const needle = query.trim().toLowerCase();
  if (!needle) return [];

  const found: DecryptedNode[] = [];
  const queue: string[] = [rootId];
  const seen = new Set<string>();

  while (queue.length && found.length < limit) {
    const folderId = queue.shift()!;
    if (seen.has(folderId)) continue;
    seen.add(folderId);

    let contents: FolderContents;
    try {
      contents = await listFolder(folderId);
    } catch {
      continue;
    }

    for (const node of contents.nodes) {
      if (node.name.toLowerCase().includes(needle)) found.push(node);
      if (node.kind === 'folder') queue.push(node.id);
    }
  }

  return found.slice(0, limit);
}
